import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useRef } from "react";
import {
  Eye, EyeOff, ArrowLeft, Phone, Calendar, MapPin,
  Hotel, PartyPopper, TreePine, Mountain, Upload, Check,
  Building2, FileText, Users, Car,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { uploadApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageUpload";

export const Route = createFileRoute("/business-signup")({
  validateSearch: (s: Record<string, unknown>) => ({
    type: typeof s.type === "string" ? s.type : undefined,
  }),
  component: BusinessSignupPage,
});

const OYO_CITIES = [
  "Ibadan", "Oyo", "Ogbomoso", "Iseyin", "Saki", "Igboho", "Kisi",
  "Eruwa", "Lanlate", "Igbo-Ora", "Fiditi", "Okeho",
];

const TYPE_META: Record<string, { icon: React.ElementType; title: string; accent: string; iconBg: string }> = {
  hotel_owner:         { icon: Hotel,        title: "Hotel",               accent: "border-primary bg-primary/5",         iconBg: "bg-primary/10 text-primary" },
  event_centre_owner:  { icon: PartyPopper,  title: "Event Centre",        accent: "border-violet-500 bg-violet-50/60",   iconBg: "bg-violet-100 text-violet-600" },
  open_space_owner:    { icon: TreePine,     title: "Open Space",          accent: "border-emerald-500 bg-emerald-50/60", iconBg: "bg-emerald-100 text-emerald-600" },
  resort_owner:        { icon: Mountain,     title: "Resort",              accent: "border-amber-500 bg-amber-50/60",     iconBg: "bg-amber-100 text-amber-600" },
  apartment_owner:     { icon: Building2,    title: "Apartments",            accent: "border-sky-500 bg-sky-50/60",          iconBg: "bg-sky-100 text-sky-600" },
  conference_hall_owner: { icon: Users,     title: "Conference Hall",       accent: "border-rose-500 bg-rose-50/60",        iconBg: "bg-rose-100 text-rose-600" },
};

function BusinessSignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();

  const businessType = search.type || "hotel_owner";
  const meta = TYPE_META[businessType] || TYPE_META.hotel_owner;

  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    password: "", confirm: "", dateOfBirth: "", location: "Ibadan",
    businessName: "", cacNumber: "", cacImage: "",
    ninNumber: "", ninImage: "",
    hasEventCentre: "no",
    numberOfUnits: "", furnished: "no", rentalType: "short",
    seatingCapacity: "", parkingSpaces: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingNin, setUploadingNin] = useState(false);
  const [uploadingCac, setUploadingCac] = useState(false);
  const ninRef = useRef<HTMLInputElement>(null);
  const cacRef = useRef<HTMLInputElement>(null);

  const set = (field: string, value: string) => setForm(f => ({ ...f, [field]: value }));

  const pwRules = {
    length:    form.password.length >= 8,
    uppercase: /[A-Z]/.test(form.password),
    digit:     /[0-9]/.test(form.password),
    match:     form.password.length > 0 && form.password === form.confirm,
  };
  const pwValid = pwRules.length && pwRules.uppercase && pwRules.digit;

  const handleUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "ninImage" | "cacImage",
    setUploading: (v: boolean) => void
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const res = await uploadApi.publicImage(compressed);
      set(field, res.url);
      toast.success(`${field === "ninImage" ? "NIN" : "CAC certificate"} uploaded successfully`);
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const resolveRole = (): string => {
    if (businessType === "hotel_owner" && form.hasEventCentre === "yes") {
      return "HotelEventOwner";
    }
    const map: Record<string, string> = {
      hotel_owner: "HotelOwner",
      event_centre_owner: "EventCentreOwner",
      open_space_owner: "OpenSpaceOwner",
      resort_owner: "ResortOwner",
      apartment_owner: "ApartmentOwner",
      conference_hall_owner: "ConferenceHallOwner",
    };
    return map[businessType] || "VenueManager";
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pwValid) return toast.error("Password must be 8+ chars with an uppercase letter and a digit");
    if (form.password !== form.confirm) return toast.error("Passwords do not match");
    if (!form.phone.trim()) return toast.error("Phone number is required");
    if (!form.dateOfBirth) return toast.error("Date of birth is required");
    if (!form.businessName.trim()) return toast.error("Business name is required");
    if (!form.cacNumber.trim()) return toast.error("CAC registration number is required");
    if (!form.cacImage) return toast.error("CAC certificate upload is required");
    if (!form.ninNumber.trim()) return toast.error("NIN number is required");
    if (!form.ninImage) return toast.error("NIN ID card upload is required");

    setLoading(true);
    try {
      await signup({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password,
        confirmPassword: form.confirm,
        dateOfBirth: form.dateOfBirth,
        location: form.location,
        role: resolveRole(),
        businessName: form.businessName.trim(),
        businessType,
        cacNumber: form.cacNumber.trim(),
        cacImage: form.cacImage,
        ninNumber: form.ninNumber.trim(),
        ninImage: form.ninImage,
        hasEventCentre: businessType === "hotel_owner" ? form.hasEventCentre === "yes" : undefined,
        numberOfUnits: businessType === "apartment_owner" ? Number(form.numberOfUnits) || undefined : undefined,
        furnished: businessType === "apartment_owner" ? form.furnished === "yes" : undefined,
        rentalType: businessType === "apartment_owner" ? form.rentalType : undefined,
        seatingCapacity: businessType === "conference_hall_owner" ? Number(form.seatingCapacity) || undefined : undefined,
        parkingSpaces: businessType === "conference_hall_owner" ? Number(form.parkingSpaces) || undefined : undefined,
      });
      toast.success("Business account created! Welcome to OyoBooking.");
      navigate({ to: "/admin/dashboard" as any });
    } catch (err) {
      const raw = err instanceof Error ? err.message : "Could not create account. Please try again.";
      const msg = raw.replace(/^HTTP \d+:\s*/i, "");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "h-12 rounded-xl border-border/80 bg-background px-4 text-base font-medium placeholder:text-muted-foreground/50 focus-visible:ring-2 focus-visible:ring-primary/30";

  return (
    <section className="mx-auto flex min-h-[calc(100vh-80px)] max-w-3xl flex-col justify-center px-4 py-10">
      <div className="w-full rounded-3xl border border-border/60 bg-card shadow-elegant overflow-hidden">
        <div className="p-8 sm:p-10">
          {/* Back + header */}
          <div className="animate-slide-l mb-7">
            <button type="button" onClick={() => navigate({ to: "/business-type" as any })}
              className="mb-5 flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="size-4" /> Back
            </button>
            <div className="flex items-center gap-3">
              <div className={`grid size-11 place-items-center rounded-xl ${meta.iconBg}`}>
                <meta.icon className="size-5" />
              </div>
              <div>
                <h1 className="font-display text-2xl font-extrabold leading-tight">{meta.title}</h1>
                <p className="text-sm text-muted-foreground">Set up your business account</p>
              </div>
            </div>
          </div>

          <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Business name */}
            <div className="animate-fade-in delay-75 space-y-1.5 sm:col-span-2">
              <Label className="text-sm font-bold flex items-center gap-1.5">
                <Building2 className="size-3.5" /> Business name <span className="text-rose-500">*</span>
              </Label>
              <Input required placeholder="e.g. Grand Oyo Hotel"
                value={form.businessName} onChange={e => set("businessName", e.target.value)} className={inputCls} />
            </div>

            {/* CAC — all */}
            <div className="animate-fade-in delay-100 space-y-1.5">
              <Label className="text-sm font-bold flex items-center gap-1.5">
                <FileText className="size-3.5" /> CAC Registration Number <span className="text-rose-500">*</span>
              </Label>
              <Input required placeholder="e.g. RC1234567"
                value={form.cacNumber} onChange={e => set("cacNumber", e.target.value)} className={inputCls} />
              <p className="text-xs text-muted-foreground">Corporate Affairs Commission registration number.</p>
            </div>

            {/* CAC Certificate Upload — all */}
            <div className="animate-fade-in delay-125 space-y-1.5">
              <Label className="text-sm font-bold">CAC Certificate Upload <span className="text-rose-500">*</span></Label>
              <input
                ref={cacRef}
                type="file"
                accept="image/*"
                className="hidden"
                title="Upload CAC certificate"
                onChange={e => handleUpload(e, "cacImage", setUploadingCac)}
              />
              <button
                type="button"
                onClick={() => cacRef.current?.click()}
                className="flex w-full items-center gap-3 rounded-xl border-2 border-dashed border-border/80 bg-background px-4 py-3 text-sm transition-colors hover:bg-accent/50"
              >
                {form.cacImage ? (
                  <>
                    <img src={form.cacImage} alt="CAC preview" className="size-8 rounded object-cover border" />
                    <span className="text-green-600 font-medium">CAC certificate uploaded</span>
                  </>
                ) : uploadingCac ? (
                  <>
                    <span className="size-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    <span className="text-muted-foreground">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="size-5 text-muted-foreground" />
                    <span className="text-muted-foreground">Click to upload CAC certificate</span>
                  </>
                )}
              </button>
            </div>

            {/* NIN — all */}
            <div className="animate-fade-in delay-150 space-y-1.5">
              <Label className="text-sm font-bold">NIN (National Identity Number) <span className="text-rose-500">*</span></Label>
              <Input required placeholder="e.g. 12345678901" maxLength={11}
                value={form.ninNumber} onChange={e => set("ninNumber", e.target.value)} className={inputCls} />
            </div>

            {/* NIN Upload — all */}
            <div className="animate-fade-in delay-175 space-y-1.5">
              <Label className="text-sm font-bold">NIN ID Card Upload <span className="text-rose-500">*</span></Label>
              <input
                ref={ninRef}
                type="file"
                accept="image/*"
                className="hidden"
                title="Upload NIN ID Card"
                onChange={e => handleUpload(e, "ninImage", setUploadingNin)}
              />
              <button
                type="button"
                onClick={() => ninRef.current?.click()}
                className="flex w-full items-center gap-3 rounded-xl border-2 border-dashed border-border/80 bg-background px-4 py-3 text-sm transition-colors hover:bg-accent/50"
              >
                {form.ninImage ? (
                  <>
                    <img src={form.ninImage} alt="NIN preview" className="size-8 rounded object-cover border" />
                    <span className="text-green-600 font-medium">NIN image uploaded</span>
                  </>
                ) : uploadingNin ? (
                  <>
                    <span className="size-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    <span className="text-muted-foreground">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="size-5 text-muted-foreground" />
                    <span className="text-muted-foreground">Click to upload NIN image</span>
                  </>
                )}
              </button>
            </div>

            {/* Hotel event-centre radio — hotel only */}
            {businessType === "hotel_owner" && (
              <div className="animate-fade-in delay-200 space-y-2 sm:col-span-2">
                <Label className="text-sm font-bold">Does your hotel also have event centre facilities?</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 rounded-xl border border-border/60 bg-background px-4 py-2.5 cursor-pointer hover:bg-accent/50 transition-colors">
                    <input
                      type="radio"
                      name="hasEventCentre"
                      value="yes"
                      checked={form.hasEventCentre === "yes"}
                      onChange={e => set("hasEventCentre", e.target.value)}
                      className="size-4 accent-primary"
                    />
                    <span className="text-sm font-medium">Yes</span>
                  </label>
                  <label className="flex items-center gap-2 rounded-xl border border-border/60 bg-background px-4 py-2.5 cursor-pointer hover:bg-accent/50 transition-colors">
                    <input
                      type="radio"
                      name="hasEventCentre"
                      value="no"
                      checked={form.hasEventCentre === "no"}
                      onChange={e => set("hasEventCentre", e.target.value)}
                      className="size-4 accent-primary"
                    />
                    <span className="text-sm font-medium">No</span>
                  </label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Hotels with event centres get combined permissions for both hotel and event management.
                </p>
              </div>
            )}

            {/* Apartments custom fields */}
            {businessType === "apartment_owner" && (
              <div className="animate-fade-in delay-200 space-y-4 sm:col-span-2">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-bold flex items-center gap-1.5">
                      <Building2 className="size-3.5" /> Number of units <span className="text-rose-500">*</span>
                    </Label>
                    <Input required type="number" min={1} placeholder="e.g. 12"
                      value={form.numberOfUnits} onChange={e => set("numberOfUnits", e.target.value)} className={inputCls} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-bold">Furnished?</Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 rounded-xl border border-border/60 bg-background px-4 py-2.5 cursor-pointer hover:bg-accent/50 transition-colors">
                        <input type="radio" name="furnished" value="yes"
                          checked={form.furnished === "yes"} onChange={e => set("furnished", e.target.value)} className="size-4 accent-primary" />
                        <span className="text-sm font-medium">Yes</span>
                      </label>
                      <label className="flex items-center gap-2 rounded-xl border border-border/60 bg-background px-4 py-2.5 cursor-pointer hover:bg-accent/50 transition-colors">
                        <input type="radio" name="furnished" value="no"
                          checked={form.furnished === "no"} onChange={e => set("furnished", e.target.value)} className="size-4 accent-primary" />
                        <span className="text-sm font-medium">No</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-bold">Rental type</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 rounded-xl border border-border/60 bg-background px-4 py-2.5 cursor-pointer hover:bg-accent/50 transition-colors">
                      <input type="radio" name="rentalType" value="short"
                        checked={form.rentalType === "short"} onChange={e => set("rentalType", e.target.value)} className="size-4 accent-primary" />
                      <span className="text-sm font-medium">Short-term</span>
                    </label>
                    <label className="flex items-center gap-2 rounded-xl border border-border/60 bg-background px-4 py-2.5 cursor-pointer hover:bg-accent/50 transition-colors">
                      <input type="radio" name="rentalType" value="long"
                        checked={form.rentalType === "long"} onChange={e => set("rentalType", e.target.value)} className="size-4 accent-primary" />
                      <span className="text-sm font-medium">Long-term</span>
                    </label>
                    <label className="flex items-center gap-2 rounded-xl border border-border/60 bg-background px-4 py-2.5 cursor-pointer hover:bg-accent/50 transition-colors">
                      <input type="radio" name="rentalType" value="both"
                        checked={form.rentalType === "both"} onChange={e => set("rentalType", e.target.value)} className="size-4 accent-primary" />
                      <span className="text-sm font-medium">Both</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Conference Hall custom fields */}
            {businessType === "conference_hall_owner" && (
              <div className="animate-fade-in delay-200 space-y-4 sm:col-span-2">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-bold flex items-center gap-1.5">
                      <Users className="size-3.5" /> Seating capacity <span className="text-rose-500">*</span>
                    </Label>
                    <Input required type="number" min={1} placeholder="e.g. 200"
                      value={form.seatingCapacity} onChange={e => set("seatingCapacity", e.target.value)} className={inputCls} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-bold flex items-center gap-1.5">
                      <Car className="size-3.5" /> Parking spaces
                    </Label>
                    <Input type="number" min={0} placeholder="e.g. 50"
                      value={form.parkingSpaces} onChange={e => set("parkingSpaces", e.target.value)} className={inputCls} />
                  </div>
                </div>
              </div>
            )}

            {/* Name row */}
            <div className="animate-fade-in delay-200 space-y-1.5">
              <Label className="text-sm font-bold">First name <span className="text-rose-500">*</span></Label>
              <Input required autoComplete="given-name" placeholder="Adaeze"
                value={form.firstName} onChange={e => set("firstName", e.target.value)} className={inputCls} />
            </div>
            <div className="animate-fade-in delay-200 space-y-1.5">
              <Label className="text-sm font-bold">Last name <span className="text-rose-500">*</span></Label>
              <Input required autoComplete="family-name" placeholder="Okafor"
                value={form.lastName} onChange={e => set("lastName", e.target.value)} className={inputCls} />
            </div>

            {/* Email */}
            <div className="animate-fade-in delay-225 space-y-1.5">
              <Label className="text-sm font-bold">Email address <span className="text-rose-500">*</span></Label>
              <Input required type="email" autoComplete="email" placeholder="you@example.com"
                value={form.email} onChange={e => set("email", e.target.value)} className={inputCls} />
            </div>

            {/* Phone */}
            <div className="animate-fade-in delay-250 space-y-1.5">
              <Label className="text-sm font-bold flex items-center gap-1.5">
                <Phone className="size-3.5" /> Phone number <span className="text-rose-500">*</span>
              </Label>
              <Input required type="tel" autoComplete="tel" placeholder="+234 800 000 0000"
                value={form.phone} onChange={e => set("phone", e.target.value)} className={inputCls} />
            </div>

            {/* DOB */}
            <div className="animate-fade-in delay-275 space-y-1.5">
              <Label className="text-sm font-bold flex items-center gap-1.5">
                <Calendar className="size-3.5" /> Date of birth <span className="text-rose-500">*</span>
              </Label>
              <Input required type="date" value={form.dateOfBirth}
                onChange={e => set("dateOfBirth", e.target.value)} className={inputCls} />
            </div>

            {/* City */}
            <div className="animate-fade-in delay-275 space-y-1.5">
              <Label className="text-sm font-bold flex items-center gap-1.5">
                <MapPin className="size-3.5" /> City <span className="text-rose-500">*</span>
              </Label>
              <select required value={form.location} onChange={e => set("location", e.target.value)}
                title="City"
                className={`${inputCls} w-full appearance-none shadow-sm`}>
                {OYO_CITIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {/* Password */}
            <div className="animate-fade-in delay-300 space-y-1.5 sm:col-span-2">
              <Label className="text-sm font-bold">Password <span className="text-rose-500">*</span></Label>
              <div className="relative">
                <Input required type={showPw ? "text" : "password"} autoComplete="new-password"
                  placeholder="Min 8 chars, 1 uppercase, 1 digit"
                  value={form.password} onChange={e => set("password", e.target.value)}
                  className={`${inputCls} pr-12`} />
                <button type="button" onClick={() => setShowPw(s => !s)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </div>

            {/* Password requirements */}
            {form.password.length > 0 && (
              <div className="animate-fade-in sm:col-span-2 -mt-1 grid grid-cols-2 gap-x-4 gap-y-1 rounded-xl border border-border/60 bg-accent/40 px-4 py-3 text-xs">
                {([
                  [pwRules.length,    "8+ characters"],
                  [pwRules.uppercase, "One uppercase letter"],
                  [pwRules.digit,     "One digit (0-9)"],
                  [pwRules.match,     "Passwords match"],
                ] as [boolean, string][]).map(([ok, label]) => (
                  <span key={label} className={`flex items-center gap-1.5 font-medium ${ok ? "text-green-600" : "text-muted-foreground"}`}>
                    <span className={`size-1.5 rounded-full ${ok ? "bg-green-500" : "bg-muted-foreground/40"}`} />
                    {label}
                  </span>
                ))}
              </div>
            )}

            {/* Confirm */}
            <div className="animate-fade-in delay-375 space-y-1.5 sm:col-span-2">
              <Label className="text-sm font-bold">Confirm password <span className="text-rose-500">*</span></Label>
              <Input required type="password" autoComplete="new-password" placeholder="Re-enter your password"
                value={form.confirm} onChange={e => set("confirm", e.target.value)} className={inputCls} />
            </div>

            <div className="animate-fade-in delay-400 pt-1 sm:col-span-2">
              <Button type="submit" disabled={loading} size="lg" className="h-12 w-full rounded-xl text-base font-bold">
                {loading ? "Creating account..." : `Create ${meta.title} Account`}
              </Button>
            </div>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-primary hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </section>
  );
}
